// PageDescription: 우리동네 병원 검색 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import NeighborhoodHospitalTemplate from '@/components/templates/hospital/neighborhood/NeighborhoodHospitalTemplate';
import { DEFAULT_NEIGHBORHOOD_CATEGORY_KEYWORD, SEOUL_REGION_CODE } from '@/constants/hospital';
import { Region } from '@/entities/region';
import useSearchHospitalsGenerally from '@/hooks/hospital/list/useSearchHospitalsGenerally';
import useMedicalCategories from '@/hooks/hospital/main/useMedicalCategories';
import useNeighborhoodHospitalViewModel from '@/hooks/hospital/neighborhood/useNeighborhoodHospitalViewModel';
import useSearchKoreaAddressV2 from '@/hooks/hospital/search/useSearchKoreaAddressV2';
import useGetKeywordSearchMagazineInfinite from '@/hooks/search/magazine/useGetKeywordSearchMagazineInfinite';
import useInfiniteSearchNonF2fDoctors from '@/hooks/search/useInfiniteSearchNonF2fDoctors';
import { NeighborhoodHospitalFilter, NeighborhoodHospitalType } from '@/models/hospital';
import { CustomPageProps } from '@/types/app';
import {
  DoctorSortType,
  DoctorType,
  MedicalCategorySortType,
  TelemedicineEligibleTargetType,
} from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { NeighborhoodHospitalQueries } from '@/types/hospital';
import { MagazineType } from '@/types/magazine';
import { addQueryString } from '@/utils/url';

const NeighborhoodHospitalPage = ({ client }: CustomPageProps) => {
  const viewModel = useNeighborhoodHospitalViewModel(client);

  const { title, description, canonical } = viewModel.getNeighborhoodSeoProps();

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        openGraph={{
          title,
          description: description,
          url: canonical,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_MAIN_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <NeighborhoodHospitalTemplate viewModel={viewModel} client={client} />
    </>
  );
};

NeighborhoodHospitalPage.Layout = DefaultLayout;

export default NeighborhoodHospitalPage;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const queryClient = new QueryClient();
  const queries = ctx.query as NeighborhoodHospitalQueries;
  const { filter = NeighborhoodHospitalFilter.F2fHospital } = queries;

  /* 쿼리가 없는 경우 리다이렉트 */
  if (!queries.keyword || !queries.regionCode || !queries.type) {
    return {
      redirect: {
        permanent: true,
        destination:
          ROUTES.HOSPITAL.NEIGHBORHOOD.path +
          addQueryString({
            keyword: encodeURIComponent(DEFAULT_NEIGHBORHOOD_CATEGORY_KEYWORD),
            regionCode: SEOUL_REGION_CODE,
            type: NeighborhoodHospitalType.NightOperation,
          }),
      },
    };
  }

  const departments = await useMedicalCategories.fetchQuery(queryClient, MedicalCategorySortType.Alphabet);

  const type = queries.type;
  const keyword = queries.keyword;

  if (!departments.find((department) => department.name === keyword)) {
    return {
      redirect: {
        permanent: false,
        destination:
          ROUTES.HOSPITAL.NEIGHBORHOOD.path +
          addQueryString({
            keyword: encodeURIComponent(DEFAULT_NEIGHBORHOOD_CATEGORY_KEYWORD),
            regionCode: SEOUL_REGION_CODE,
            type: NeighborhoodHospitalType.NightOperation,
          }),
      },
    };
  }

  const queryRegion = new Region(queries.regionCode);

  await Promise.allSettled([
    useSearchKoreaAddressV2.prefetchQuery(queryClient, {
      siDoCode: null,
      siGunGuCode: null,
    }),
    useSearchKoreaAddressV2.prefetchQuery(queryClient, {
      siDoCode: queryRegion.excludeSejongSiDoRegionCode(),
      siGunGuCode: null,
    }),
    useSearchKoreaAddressV2.prefetchQuery(queryClient, {
      siDoCode: queryRegion.regionCodes.siDoCode,
      siGunGuCode: queryRegion.regionCodes.siGunGuCode,
    }),
    useGetKeywordSearchMagazineInfinite.prefetchQuery(queryClient, {
      keyword: keyword,
      size: 10,
      contentType: MagazineType.FAQ,
    }),
    useGetKeywordSearchMagazineInfinite.prefetchQuery(queryClient, {
      keyword: keyword,
      size: 10,
      contentType: MagazineType.ARTICLE,
    }),
    useSearchHospitalsGenerally.prefetchInfinityQuery(
      queryClient,
      useSearchHospitalsGenerally.parseParameterByNeighborhoodHospital({
        keyword: keyword,
        regionCode: queries.regionCode,
        filter,
        hospitalType: type,
        size: 10,
      })
    ),
    useInfiniteSearchNonF2fDoctors.prefetchQuery(queryClient, {
      request: {
        keyword: keyword,
        sortType: DoctorSortType.Random,
        page: 1,
        limit: 1,
        doctorType: DoctorType.Doctor,
        telemedicineEligibleTargetType: TelemedicineEligibleTargetType.AlwaysEligible,
      },
    }),
  ]);

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};
